import React from "react";
import moment from "moment";

import { CreditsList, Credit, CreditHeading } from "models";
import { ISimpleGridColumns, GridStyles, SimpleGrid } from "component-library";
import { IPaperCVContent } from "../PaperCVContent";

interface IPerformerCreditsGridProps {
  creditsList: CreditsList;
  content: IPaperCVContent;
}

declare type GridCreditType = Pick<
  Credit,
  "id" | "name" | "dateFrom" | "typeId" | "venue" | "company" | "director"
>;

const CREDITS_GRID: ISimpleGridColumns<GridCreditType> = {
  id: "hide",
  name: {
    type: "string",
    label: "",
    width: 3,
    formatCell: (value: string) => (
      <div className="c-paper-cv__credit-grid-credit-title">{value}</div>
    ),
  },
  director: {
    type: "string",
    label: "",
    width: 2,
  },
  dateFrom: {
    type: "string",
    label: "",
    width: 1,
  },
  typeId: {
    type: "string",
    label: "",
    width: 2,
  },
  company: {
    type: "string",
    label: "",
    width: 2,
  },
  venue: {
    type: "string",
    label: "",
    width: 1,
  },
};

const SET_GRID_LABELS = (content: IPaperCVContent) => {
  (CREDITS_GRID.company as any).label = content.companyLabel;
  (CREDITS_GRID.dateFrom as any).label = content.dateLabel;
  (CREDITS_GRID.director as any).label = content.directorLabel;
  (CREDITS_GRID.name as any).label = content.prodAndRoleNameLabel;
  (CREDITS_GRID.typeId as any).label = content.prodTypeLabel;
};

const RENDER_TYPE = (headings: CreditHeading[]) => (value: any) => {
  const heading = headings.find((heading) => heading.creditType.id === value);

  return heading ? heading.creditType.name : "-";
};

const RENDER_PRODUCTION_NAME = (value: any, credit: Credit) => {
  value = (
    <div className="c-paper-cv__credit-prod-name">
      <span className="c-paper-cv__credit-name">{credit.name}</span>
      <span>{credit.role}</span>
    </div>
  );

  return value;
};

const FORMAT_TIME = (date?: Date, granularity?: string) => {
  let format = "DD/MM/YYYY";

  if (granularity === "m") {
    format = "MM/YYYY";
  } else if (granularity === "y") {
    format = "YYYY";
  }

  return date && moment(date).format(format);
};

const RENDER_DATE = (value: any, credit: Credit) => {
  const fromDate = FORMAT_TIME(credit.dateFrom, credit.dateFromFormat);
  const toDate = FORMAT_TIME(credit.dateTo, credit.dateToFormat);

  const isDashVisible = Boolean(fromDate) && Boolean(toDate);

  value = `${fromDate || ""}${isDashVisible ? "-" : ""}${toDate || ""}`;

  return value;
};

const GET_GRID_STYLES = () => {
  const gridStyles = new GridStyles();
  gridStyles.body = "c-paper-cv__credit-grid-body";
  gridStyles.bodyCell = "c-paper-cv__credit-grid-body-cell";
  gridStyles.bodyRow = "c-paper-cv__credit-grid-body-row";
  gridStyles.header = "c-paper-cv__credit-grid-header";
  gridStyles.headerCell = "c-paper-cv__credit-grid-header-cell";
  gridStyles.headerLabel = "c-paper-cv__credit-grid-header-label";
  gridStyles.headerRow = "c-paper-cv__credit-grid-header-row";
  gridStyles.headerTitle = "c-paper-cv__credit-grid-header-title";

  return gridStyles;
};

export const PerformerCreditsGrid: React.FC<
  React.PropsWithChildren<IPerformerCreditsGridProps>
> = ({ creditsList, content }) => {
  const headings = creditsList.creditHeadings
    .filter((heading) => !heading.isHidden)
    .sort((left, right) => left.position - right.position);

  SET_GRID_LABELS(content);

  (CREDITS_GRID.name as any).formatCell = RENDER_PRODUCTION_NAME;
  (CREDITS_GRID.dateFrom as any).formatCell = RENDER_DATE;
  (CREDITS_GRID.typeId as any).formatCell = RENDER_TYPE(
    creditsList.creditHeadings
  );

  const furtherCredits = creditsList.credits
    .filter(
      (credit) =>
        !headings.some((heading) => heading.creditType.id === credit.typeId)
    )
    .sort((left, right) => left.position - right.position);
  const hasFurtherCredits = furtherCredits.length > 0;

  return (
    <div>
      {headings.map((headings) => {
        const credits = creditsList.credits
          .filter((credit) => credit.typeId === headings.creditType.id)
          .sort((left, right) => left.position - right.position);

        return (
          <React.Fragment key={headings.creditType.id}>
            <div className="c-paper-cv__credit-header">
              {headings.creditType.name}
            </div>
            <div>
              <SimpleGrid<GridCreditType>
                data={credits}
                styles={GET_GRID_STYLES()}
                columns={CREDITS_GRID}
                maxHeight="10000000px"
              />
            </div>
          </React.Fragment>
        );
      })}
      {hasFurtherCredits && (
        <React.Fragment>
          <div className="c-paper-cv__credit-header">
            {content.furtherCredits}
          </div>
          <div>
            <SimpleGrid<GridCreditType>
              data={furtherCredits}
              styles={GET_GRID_STYLES()}
              columns={CREDITS_GRID}
              maxHeight="10000000px"
            />
          </div>
        </React.Fragment>
      )}
    </div>
  );
};
